import React, { useState } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "../../design/design-system";
import Text from "../../atoms/Text";
import Button from "../../atoms/Button";
import { evaluationApi } from "../../../services/api/aiReviewTest/evaluationApi";
import { ToastManager, WaitingManager } from "../../organisms/CommonUI";
import Markdown from "react-markdown";
import Icon from "../../atoms/Icon";

const EvaluationResultContainer = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const EvaluationSection = styled.div`
  margin-bottom: 24px;
`;

const EvaluationTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  margin-bottom: 16px;
`;

const EvaluationInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 20px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
`;

const EvaluationInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EvaluationLabel = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const EvaluationValue = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  font-weight: 500;
`;

const ScoreSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 20px;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  margin-bottom: 24px;
`;

const ScoreInfo = styled.div`
  flex: 1;
`;

const ScoreLabel = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  margin-bottom: 4px;
`;

const ScoreValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Red")?.[600]};
`;

const EvaluationCommentBox = styled.div`
  padding: 20px;
  background: #fafbfc;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  margin-bottom: 24px;
`;

const MarkdownContent = styled.div`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  line-height: 1.8;
  background: transparent;

  p {
    margin: 0 0 12px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 0 0 12px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 4px;
  }

  strong {
    font-weight: 600;
    color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  }

  code {
    background: transparent;
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 13px;
  }

  pre {
    background: transparent;
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;

    code {
      background: none;
      padding: 0;
    }
  }

  blockquote {
    border-left: 3px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
    padding-left: 16px;
    margin: 12px 0;
    color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 16px 0 12px 0;
    font-weight: 600;
    color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  }

  h1 {
    font-size: 20px;
  }
  h2 {
    font-size: 18px;
  }
  h3 {
    font-size: 16px;
  }
  h4 {
    font-size: 15px;
  }
  h5 {
    font-size: 14px;
  }
  h6 {
    font-size: 13px;
  }
`;

const NoCommentText = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  font-style: italic;
`;

const ErrorMessageBox = styled.div`
  padding: 16px;
  background: ${COLOR_SYSTEM.get("Red")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Red")?.[200]};
  border-radius: 8px;
  margin-top: 24px;
`;

const ErrorTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Red")?.[700]};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ErrorContent = styled.div`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Red")?.[800]};
  line-height: 1.6;
  white-space: pre-wrap;
`;

const NoEvaluationText = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  font-style: italic;
  text-align: center;
  padding: 40px;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 32px;
`;

const CollapsibleSection = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  overflow: hidden;
`;

const CollapsibleHeader = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const CollapsibleTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const CollapsibleContent = styled.div<{ isOpen: boolean }>`
  max-height: ${({ isOpen }) => (isOpen ? "100vh" : "0")};
  overflow-y: auto;
  transition: max-height 0.3s ease-in-out;
  background: white;

  & > div {
    padding: 16px;
  }
`;

const GroundTruthBox = styled.div`
  padding: 16px;
  border-radius: 6px;
`;

interface EvaluationResultProps {
  evaluation: any; // AIReviewEvaluation 타입 (errorMessage 포함)
}

const EvaluationResult: React.FC<EvaluationResultProps> = ({ evaluation }) => {
  const [isReevaluating, setIsReevaluating] = useState(false);
  const [isGroundTruthOpen, setIsGroundTruthOpen] = useState(false); // 기본값을 false로 변경하여 닫힌 상태로 시작

  // 마크다운 텍스트 정리 함수
  const cleanMarkdown = (text: string) => {
    if (!text) return text;

    // 코드 블록으로 전체가 감싸진 경우 처리
    const codeBlockRegex = /^```[\s\S]*?\n([\s\S]*?)\n```$/;
    const match = text.match(codeBlockRegex);
    if (match) {
      return match[1];
    }

    // 모든 줄이 동일한 들여쓰기를 가진 경우 처리
    const lines = text.split("\n");
    const minIndent = Math.min(
      ...lines
        .filter((line) => line.trim().length > 0)
        .map((line) => line.match(/^(\s*)/)?.[1].length || 0)
    );

    if (minIndent > 0) {
      return lines.map((line) => line.substring(minIndent)).join("\n");
    }

    return text;
  };

  const handleReevaluate = async () => {
    if (!evaluation || isReevaluating) return;

    try {
      setIsReevaluating(true);
      WaitingManager.show({ message: "재평가를 실행하고 있습니다..." });

      // 재평가 API 호출
      const response = await evaluationApi.reEvaluate(evaluation.id);

      if (response.data.error === 0) {
        ToastManager.show({
          content: "재평가가 시작되었습니다.",
          duration: 3000,
        });

        // 재평가가 시작되면 창 닫기
        setTimeout(() => {
          window.close();
        }, 1500);
      }
    } catch (error) {
      console.error("재평가 실행 실패:", error);
      ToastManager.show({
        content: "재평가 실행에 실패했습니다.",
        duration: 3000,
      });
    } finally {
      setIsReevaluating(false);
      WaitingManager.hide();
    }
  };

  if (!evaluation) {
    return (
      <EvaluationResultContainer>
        <NoEvaluationText>평가 결과가 없습니다.</NoEvaluationText>
      </EvaluationResultContainer>
    );
  }

  return (
    <EvaluationResultContainer>
      {/* 평가 점수 및 재평가 버튼 */}
      <ScoreSection>
        <ScoreInfo>
          <ScoreLabel>평가 점수</ScoreLabel>
          <ScoreValue>
            {evaluation.score !== null && evaluation.score !== undefined
              ? `${Math.round(evaluation.score)}점`
              : "미평가"}
          </ScoreValue>
        </ScoreInfo>
        <Button
          size="Medium"
          type="IconWithText"
          variant="Contained"
          color="Primary"
          icon="Refresh"
          left
          onClick={handleReevaluate}
          disabled={isReevaluating}
        >
          {isReevaluating ? "재평가 중..." : "재평가 실행"}
        </Button>
      </ScoreSection>

      {/* Ground Truth Snapshot - 상단으로 이동 */}
      {evaluation.groundTruthSnapshot && (
        <CollapsibleSection>
          <CollapsibleHeader onClick={() => setIsGroundTruthOpen(!isGroundTruthOpen)}>
            <CollapsibleTitle>정답(Ground Truth Snapshot)</CollapsibleTitle>
            <Icon
              name={isGroundTruthOpen ? "Chevron Up" : "Chevron Down"}
              size={20}
              color={COLOR_SYSTEM.get("Gray")?.[600]}
            />
          </CollapsibleHeader>
          <CollapsibleContent isOpen={isGroundTruthOpen}>
            <div>
              <GroundTruthBox>
                <MarkdownContent>
                  <Markdown>{cleanMarkdown(evaluation.groundTruthSnapshot)}</Markdown>
                </MarkdownContent>
              </GroundTruthBox>
            </div>
          </CollapsibleContent>
        </CollapsibleSection>
      )}

      {/* 평가 의견 */}
      {evaluation.evaluationComments ? (
        <EvaluationCommentBox>
          <MarkdownContent>
            <Markdown>{cleanMarkdown(evaluation.evaluationComments)}</Markdown>
          </MarkdownContent>
        </EvaluationCommentBox>
      ) : (
        <EvaluationCommentBox>
          <NoCommentText>평가 의견이 작성되지 않았습니다.</NoCommentText>
        </EvaluationCommentBox>
      )}

      {/* 평가 정보 */}
      <EvaluationSection>
        <EvaluationTitle>평가 정보</EvaluationTitle>
        <EvaluationInfoGrid>
          <EvaluationInfoItem>
            <EvaluationLabel>평가 ID</EvaluationLabel>
            <EvaluationValue>{evaluation.id}</EvaluationValue>
          </EvaluationInfoItem>
          <EvaluationInfoItem>
            <EvaluationLabel>평가 그룹 ID</EvaluationLabel>
            <EvaluationValue>{evaluation.evaluationGroupId || "-"}</EvaluationValue>
          </EvaluationInfoItem>
          <EvaluationInfoItem>
            <EvaluationLabel>실험 ID</EvaluationLabel>
            <EvaluationValue>{evaluation.experimentId || "-"}</EvaluationValue>
          </EvaluationInfoItem>
          <EvaluationInfoItem>
            <EvaluationLabel>생성일시</EvaluationLabel>
            <EvaluationValue>
              {new Date(evaluation.createdAt).toLocaleString("ko-KR")}
            </EvaluationValue>
          </EvaluationInfoItem>
          {evaluation.evaluatedAt && (
            <EvaluationInfoItem>
              <EvaluationLabel>평가 시점</EvaluationLabel>
              <EvaluationValue>
                {new Date(evaluation.evaluatedAt).toLocaleString("ko-KR")}
              </EvaluationValue>
            </EvaluationInfoItem>
          )}
        </EvaluationInfoGrid>
      </EvaluationSection>

      {/* errorMessage가 있을 때만 하단에 에러 메시지 표시 */}
      {evaluation.errorMessage && (
        <ErrorMessageBox>
          <ErrorTitle>
            <Icon name="Alert Circle" size={18} color={COLOR_SYSTEM.get("Red")?.[600]} />
            평가 중 오류가 발생했습니다
          </ErrorTitle>
          <ErrorContent>
            {evaluation.errorMessage}
          </ErrorContent>
        </ErrorMessageBox>
      )}
    </EvaluationResultContainer>
  );
};

export default EvaluationResult;
